/**
 * Whether a Team can read the tracker backlog it was just given, said once
 * after a create or an attach. The server reports `backlogAccess` (see
 * `TeamBacklogAccess`); only the two states that leave the Team unable to read
 * the backlog yet need a word: an admin has to open the project, or Jira has
 * not finished its first sync. A Jira filter or query reaches several
 * projects, and names every one an admin has to open in one line.
 */
import type { TeamBacklogAccess } from "@yagni/shared";

import { BACKLOG_SOURCE_WORD, backlogNeedsAdminLine } from "./copy.js";

export interface BacklogAccessEntry {
  source: string;
  ref: string;
  access: TeamBacklogAccess;
  containerName?: string;
  /** Each project a Jira filter or query reaches, as far as the person may see. */
  containers?: Array<{ name: string; access: TeamBacklogAccess }>;
}

const ACCESS_STATES: readonly TeamBacklogAccess[] = [
  "already_visible",
  "opened_to_workspace",
  "granted_to_team",
  "needs_admin",
  "not_synced",
  "not_applicable",
];

function readEntry(raw: unknown, fallback?: { source: string; ref: string }): BacklogAccessEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const entry = raw as Record<string, unknown>;
  const access = ACCESS_STATES.find((state) => state === entry.access);
  const source = typeof entry.source === "string" ? entry.source : fallback?.source;
  const ref = typeof entry.ref === "string" ? entry.ref : fallback?.ref;
  if (!access || !source || !ref) return null;
  const containers = Array.isArray(entry.containers)
    ? entry.containers.flatMap((raw) => {
        const c = raw as Record<string, unknown> | null;
        const state = ACCESS_STATES.find((s) => s === c?.access);
        return state && typeof c?.name === "string" && c.name ? [{ name: c.name, access: state }] : [];
      })
    : [];
  return {
    source,
    ref,
    access,
    ...(typeof entry.containerName === "string" && entry.containerName ? { containerName: entry.containerName } : {}),
    ...(containers.length > 0 ? { containers } : {}),
  };
}

/**
 * The access entries a response carries: the create 201's
 * `setup.backlogAccess` array, or the attach 201's single `backlogAccess`
 * (which names no source or ref, so the caller passes the attachment it sent).
 * Empty when the server sent none, as an older backend does.
 */
export function readBacklogAccess(
  payload: unknown,
  attached?: { source: string; ref: string },
): BacklogAccessEntry[] {
  if (!payload || typeof payload !== "object") return [];
  const body = payload as { backlogAccess?: unknown; setup?: { backlogAccess?: unknown } };
  const fromSetup = body.setup?.backlogAccess;
  if (Array.isArray(fromSetup)) {
    return fromSetup.map((raw) => readEntry(raw)).filter((entry): entry is BacklogAccessEntry => entry !== null);
  }
  if (Array.isArray(body.backlogAccess)) {
    return body.backlogAccess
      .map((raw) => readEntry(raw))
      .filter((entry): entry is BacklogAccessEntry => entry !== null);
  }
  const single = readEntry(body.backlogAccess, attached);
  return single ? [single] : [];
}

function accessLine(source: string, access: TeamBacklogAccess, what: string): string | null {
  if (access === "needs_admin") return backlogNeedsAdminLine([what]);
  if (access === "not_synced") {
    const tracker = BACKLOG_SOURCE_WORD[source] ?? source;
    return `${tracker} is still syncing. The Team reads it once the first sync lands.`;
  }
  return null;
}

/**
 * The lines to say, none when the Team can already read it: for a query, one
 * naming every project an admin has to open (the same words the Create page
 * said before it) and one if Jira is still syncing; else one for the backlog.
 */
export function backlogAccessLines(entry: BacklogAccessEntry, name?: string | null): string[] {
  const lines = new Set<string>();
  const closed = (entry.containers ?? []).filter((c) => c.access === "needs_admin").map((c) => c.name);
  if (closed.length > 0) lines.add(backlogNeedsAdminLine(closed));
  for (const container of entry.containers ?? []) {
    if (container.access === "needs_admin") continue;
    const line = accessLine(entry.source, container.access, container.name);
    if (line) lines.add(line);
  }
  if (lines.size === 0) {
    const what = entry.containerName ?? name?.trim() ?? entry.ref;
    const line = accessLine(entry.source, entry.access, what || entry.ref);
    if (line) lines.add(line);
  }
  return [...lines];
}

/** The lines as one, or null when the Team can already read it. */
export function backlogAccessMessage(entry: BacklogAccessEntry, name?: string | null): string | null {
  const lines = backlogAccessLines(entry, name);
  return lines.length > 0 ? lines.join(" ") : null;
}

/** The notice under a create or an attach; renders nothing when there is nothing to say. */
export function BacklogAccessNotice({
  entry,
  name,
  className = "tm-field-note",
}: {
  entry: BacklogAccessEntry;
  /** The backlog's label, for when the server did not name the project. */
  name?: string | null;
  className?: string;
}) {
  const lines = backlogAccessLines(entry, name);
  if (lines.length === 0) return null;
  return (
    <>
      {lines.map((line) => (
        <p key={line} className={className} role="status" data-testid="backlog-access-notice" data-access={entry.access}>
          {line}
        </p>
      ))}
    </>
  );
}
